/**
 * The Admin's **Operadores** console — pure view-model (W8-OPERATORS-AND-SCOPES, D449, ADR-0078; findings A-05, A-06).
 *
 * The screen invites an operator, grants a role at a scope with an optional financial limit, revokes it, deactivates or
 * reactivates the account, ends its sessions and resets its second factor, and works the four-eyes queue of admin roles.
 * This module holds what can be tested without React: the form normalization (the same rules the server enforces, so the
 * operator sees the problem before a round trip), which roles need a second pair of eyes, and who may decide a request.
 * The server stays the authority on every rule — scope, limit, self-action, last super admin.
 */
import { type Result } from '@vello/core';
import { ADMIN_ROLES, isFourEyesAdminRole, roleAllowsScopeType } from '@vello/rbac';
import { grantsAllow } from './console-access.js';
import {
  type AdministrativeRegionView,
  OPERATOR_ACTION_REASON_CODES, type GrantOperatorRoleInput, type InviteOperatorInput, type ManagedOperator, type ManagedScopeType, type OperatorGrant,
  type OperatorActionReasonCode, type OperatorGrantRequest,
} from '@vello/api-client';

export { OPERATOR_ACTION_REASON_CODES, isFourEyesAdminRole };
export type { OperatorActionReasonCode };

/**
 * The geographic scopes a console grant is written at (tenant personas are granted by their own consoles): the platform, a country, a
 * state (`REGION`, ADR-0124) or a city - the order of the hierarchy.
 */
export const GRANT_SCOPE_TYPES = ['GLOBAL', 'COUNTRY', 'REGION', 'CITY'] as const satisfies readonly ManagedScopeType[];
export type GrantScopeType = (typeof GRANT_SCOPE_TYPES)[number];

/** The roles the console offers (those a geographic scope can carry), in the RBAC catalog's order. */
export const GRANTABLE_ROLES: readonly string[] = ADMIN_ROLES.filter((r) => GRANT_SCOPE_TYPES.some((s) => roleAllowsScopeType(r, s)));

/** D849 (P0-009): the kind of sign-in an invitation creates - the Vello team (BUSINESS) or a store's own people (MERCHANT, the Loja). */
export const INVITE_REALMS = ['BUSINESS', 'MERCHANT'] as const;
export type InviteRealm = (typeof INVITE_REALMS)[number];

export interface InviteForm {
  /** Absent = BUSINESS (the Vello team, the API default). */
  readonly realm?: InviteRealm;
  readonly email: string;
  readonly displayName: string;
  readonly phoneE164: string;
  readonly countryCode: string;
  readonly locale: string;
  readonly timezone: string;
}

export interface GrantForm {
  readonly role: string;
  readonly scopeType: GrantScopeType;
  readonly scopeId: string;
  /** Integer minor units as typed (digits only); empty = no financial limit. */
  readonly limitMinor: string;
  readonly limitCurrency: string;
  /** The country whose states the `REGION` select lists (a form helper: never sent - a state's code carries its country). */
  readonly regionCountry?: string;
  /** D475: the custom access profile granted instead of the whole role (its base role must be `role`); empty = the whole role. */
  readonly profileId?: string;
}

export type OperatorFormIssue =
  | 'email' | 'displayName' | 'phone' | 'country' | 'locale' | 'timezone'
  | 'role' | 'scopeId' | 'scopeNotAllowed' | 'limit' | 'currency';

const EMAIL = /^[^\s@]{1,64}@[^\s@]{1,255}\.[^\s@]{2,63}$/;
const E164 = /^\+[1-9]\d{6,14}$/;
const COUNTRY = /^[A-Z]{2}$/;
const LOCALE = /^[a-z]{2,3}(-[A-Z]{2})?$/;
const MARKET = /^[a-z]{2}:[a-z0-9]+(-[a-z0-9]+)*$/;
const REGION = /^[A-Z]{2}-[A-Z0-9]{1,3}$/;
const CURRENCY = /^[A-Z]{3}$/;
const DIGITS = /^\d{1,15}$/;

export function emptyInviteForm(countryCode = '', locale = '', timezone = ''): InviteForm {
  return { email: '', displayName: '', phoneE164: '', countryCode, locale, timezone };
}

/**
 * D849 (P0-009): the kinds of sign-in this operator may create. BUSINESS needs only `operator:manage` (the screen's own gate); a
 * MERCHANT sign-in is a store's login, so it also needs `merchant_staff:manage` on the platform (the word that puts people on a store's
 * team). An unknown authority (`undefined`) offers both and lets the server refuse.
 */
export function inviteRealmChoices(grants: readonly OperatorGrant[] | undefined): readonly InviteRealm[] {
  if (grants === undefined) return INVITE_REALMS;
  return grantsAllow(grants, { permission: 'merchant_staff:manage', target: 'PLATFORM' }) ? INVITE_REALMS : ['BUSINESS'];
}

export function emptyGrantForm(): GrantForm {
  return { role: '', scopeType: 'CITY', scopeId: '', limitMinor: '', limitCurrency: '', profileId: '' };
}

/** The countries that have at least one ACTIVE state in the catalog (what the `REGION` grant's country select offers), by code. */
export function grantRegionCountries(regions: readonly AdministrativeRegionView[]): readonly string[] {
  return [...new Set(regions.filter((r) => r.status === 'ACTIVE').map((r) => r.countryCode.toUpperCase()))].sort();
}

/** The ACTIVE states of one country (what the `REGION` grant's state select offers), by name. A retired state is never offered for a new grant. */
export function grantRegionChoices(regions: readonly AdministrativeRegionView[], country: string): readonly AdministrativeRegionView[] {
  const cc = country.trim().toUpperCase();
  return regions.filter((r) => r.status === 'ACTIVE' && r.countryCode.toUpperCase() === cc).slice().sort((a, b) => a.name.localeCompare(b.name));
}

export function normalizeInviteForm(form: InviteForm, reasonCode: OperatorActionReasonCode): Result<InviteOperatorInput, readonly OperatorFormIssue[]> {
  const issues: OperatorFormIssue[] = [];
  const email = form.email.trim().toLowerCase();
  const displayName = form.displayName.trim();
  const phoneE164 = form.phoneE164.replace(/[\s()-]/g, '');
  const countryCode = form.countryCode.trim().toUpperCase();
  const locale = form.locale.trim();
  const timezone = form.timezone.trim();
  if (!EMAIL.test(email)) issues.push('email');
  if (displayName === '' || displayName.length > 120) issues.push('displayName');
  if (!E164.test(phoneE164)) issues.push('phone');
  if (!COUNTRY.test(countryCode)) issues.push('country');
  if (locale !== '' && !LOCALE.test(locale)) issues.push('locale');
  if (timezone !== '' && !validTimeZone(timezone)) issues.push('timezone');
  if (issues.length > 0) return { ok: false, error: issues };
  return {
    ok: true,
    value: { email, displayName, phoneE164, countryCode, ...(locale !== '' ? { locale } : {}), ...(timezone !== '' ? { timezone } : {}), ...(form.realm === 'MERCHANT' ? { realm: 'MERCHANT' as const } : {}), reasonCode },
  };
}

/**
 * `regions` is the state catalog when the screen holds it: a `REGION` grant must then name one of its ACTIVE states (the server refuses an
 * unregistered one too - this only shows the problem before the round trip). Without the catalog only the code's shape is checked.
 */
export function normalizeGrantForm(form: GrantForm, reasonCode: OperatorActionReasonCode, regions?: readonly AdministrativeRegionView[]): Result<GrantOperatorRoleInput, readonly OperatorFormIssue[]> {
  const issues: OperatorFormIssue[] = [];
  const role = form.role;
  const upper = form.scopeType === 'COUNTRY' || form.scopeType === 'REGION';
  const scopeId = upper ? form.scopeId.trim().toUpperCase() : form.scopeId.trim().toLowerCase();
  if (!(GRANTABLE_ROLES as readonly string[]).includes(role)) issues.push('role');
  else if (!roleAllowsScopeType(role, form.scopeType)) issues.push('scopeNotAllowed');
  if (form.scopeType === 'COUNTRY' && !COUNTRY.test(scopeId)) issues.push('scopeId');
  if (form.scopeType === 'REGION' && (!REGION.test(scopeId) || (regions !== undefined && !regions.some((r) => r.code.toUpperCase() === scopeId && r.status === 'ACTIVE')))) issues.push('scopeId');
  if (form.scopeType === 'CITY' && !MARKET.test(scopeId)) issues.push('scopeId');
  const limit = form.limitMinor.trim();
  const currency = form.limitCurrency.trim().toUpperCase();
  if (limit !== '' && !DIGITS.test(limit)) issues.push('limit');
  if (limit !== '' && !CURRENCY.test(currency)) issues.push('currency');
  if (issues.length > 0) return { ok: false, error: issues };
  return {
    ok: true,
    value: {
      role,
      scopeType: form.scopeType,
      ...(form.scopeType !== 'GLOBAL' ? { scopeId } : {}),
      ...(limit !== '' ? { financialLimitMinor: String(BigInt(limit)), financialLimitCurrency: currency } : {}),
      ...(form.profileId !== undefined && form.profileId !== '' ? { profileId: form.profileId } : {}),
      reasonCode,
    },
  };
}

function validTimeZone(tz: string): boolean {
  try {
    new Intl.DateTimeFormat('en', { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

/**
 * The request a reader may approve or reject: never its own proposal (the server and the database refuse it too). D868: a GLOBAL
 * super administrator (`selfApproval`) approves their own proposal - never one that gives the role to themselves (`readerId`).
 */
export function canDecideGrantRequest(request: OperatorGrantRequest, selfApproval = false, readerId?: string): boolean {
  if (request.state !== 'PENDING') return false;
  if (request.mine !== true) return true;
  return selfApproval && readerId !== undefined && request.userId.trim().toLowerCase() !== readerId.trim().toLowerCase();
}

/** The actions an operator row offers, by its status (the server refuses a self-action; the screen hides it). */
export interface OperatorRowActions {
  readonly deactivate: boolean;
  readonly reactivate: boolean;
  readonly revokeSessions: boolean;
  readonly resetMfa: boolean;
  readonly reissueInvitation: boolean;
}

export function operatorRowActions(op: ManagedOperator, selfUserId: string | undefined): OperatorRowActions {
  const self = selfUserId !== undefined && op.userId.toLowerCase() === selfUserId.toLowerCase();
  return {
    deactivate: !self && op.status !== 'DEACTIVATED',
    reactivate: !self && op.status === 'DEACTIVATED',
    revokeSessions: !self && op.status === 'ACTIVE',
    resetMfa: !self && op.status === 'ACTIVE',
    reissueInvitation: !self && op.status === 'INVITED',
  };
}

/** The invitation token of the landing link (`/invitation?token=…`), or '' when absent. */
export function invitationTokenOf(search: string): string {
  try {
    return new URLSearchParams(search).get('token') ?? '';
  } catch {
    return '';
  }
}

/** A person to give a sign-in: the e-mail is the key; `name` and `phoneE164` are only needed when the e-mail has no login yet. */
export interface LoginCandidate {
  readonly email: string;
  readonly name: string;
  readonly phoneE164: string;
}

/** What to do with a person's e-mail given the operators the console can see (D849). */
export type LoginResolution =
  | { readonly kind: 'existing'; readonly userId: string; readonly status: ManagedOperator['status'] }
  | { readonly kind: 'invite'; readonly invite: InviteOperatorInput }
  | { readonly kind: 'refused'; readonly reason: 'WRONG_REALM' | 'DEACTIVATED' | 'NEEDS_DETAILS' };

const INVITE_E164 = /^\+[1-9]\d{6,14}$/;

/**
 * D849 (P0-009/010): find the login of `candidate` in the realm the role needs, or say how to create it. A login of the OTHER realm is never
 * reused (a store login cannot open the Frota console, a team login cannot open the Loja), and a deactivated one is never revived here.
 */
export function resolveLogin(candidate: LoginCandidate, operators: readonly ManagedOperator[], target: { readonly realm: InviteRealm; readonly countryCode: string; readonly locale?: string; readonly timezone?: string; readonly reasonCode: OperatorActionReasonCode }): LoginResolution {
  const found = operators.find((o) => o.identifier.trim().toLowerCase() === candidate.email.trim().toLowerCase());
  if (found !== undefined) {
    if (found.realm !== target.realm) return { kind: 'refused', reason: 'WRONG_REALM' };
    if (found.status === 'DEACTIVATED') return { kind: 'refused', reason: 'DEACTIVATED' };
    return { kind: 'existing', userId: found.userId, status: found.status };
  }
  if (candidate.name.trim() === '' || !INVITE_E164.test(candidate.phoneE164)) return { kind: 'refused', reason: 'NEEDS_DETAILS' };
  return {
    kind: 'invite',
    invite: {
      email: candidate.email.trim().toLowerCase(), displayName: candidate.name.trim(), phoneE164: candidate.phoneE164, countryCode: target.countryCode,
      ...(target.locale !== undefined && target.locale !== '' ? { locale: target.locale } : {}), ...(target.timezone !== undefined && target.timezone !== '' ? { timezone: target.timezone } : {}),
      ...(target.realm === 'MERCHANT' ? { realm: 'MERCHANT' as const } : {}), reasonCode: target.reasonCode,
    },
  };
}
